import './alteration-area.scss'

import _ from 'lodash'
import { defineComponent, PropType } from 'vue'
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons'

import { getTranslatedString, Alteration, ThesaurusTerm } from '@skinsoft/types'
import { ThesaurusTermDataStore } from '@skinsoft/entity'

import { reactiveVueMixin } from "@skinsoft/reactive-vue"
import ConditionReportMediasArea from '../condition-report-medias-area'
import { DELETE_IMAGE_CLICK_TOPIC, MEDIA_ID_CHANGED_FROM_CACHE, PRINCIPAL_IMAGE_CLICK_TOPIC } from '@skinsoft/bus'
import { ALTERATION_EDITED, DELETE_ALTERATION } from '../drawing-editor-components/alteration-line'
import { ALTERATION_CREATED } from './create-alteration-form'
import { ALTERATION_CARD_ALTERATION_UPDATED, ALTERATION_CARD_DELETE_ALTERATION, AlterationWithThesaurusTerm } from './alteration-card'
import AlterationCardGroup, { GroupingAlterationField } from './alteration-card-group'

const MEDIA_VIEW = 'media-vue'
const ALTERATION_TYPE_VIEW = 'alteration-type-view'
const ALTERATION_TITLE_VIEW = 'alteration-title-view'

export default defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sConditionReportMediasArea: ConditionReportMediasArea,
        sAlterationCardGroup: AlterationCardGroup,
    },
    props: {
        form: {},
        fieldName: {},
        associatedMedias: {},
        alterations: {
            type: Array as PropType<Alteration[]>
        },
        readOnly: { type: Boolean },
        existingTitles: {
            type: Array as PropType<String[]>,
            default: () => []
        },
    },
    template: require('./alteration-area.html'),
    data() {
        return {
            menuBurgerIcon: faBars,
            closeIcon: faTimes,

            alterationsByAlterationType: {} as { [alterationTypeId: string]: (AlterationWithThesaurusTerm & { index?: number })[] },
            alterationsByTitle: {} as { [title: string]: (AlterationWithThesaurusTerm & { index?: number })[] },

            sections: [MEDIA_VIEW, ALTERATION_TYPE_VIEW, ALTERATION_TITLE_VIEW] as string[],
            activeSection: 0,

            isViewMenuOpen: false,

            GROUP_BY_ALTERATION_TYPE: GroupingAlterationField.AlterationType,
            GROUP_BY_TITLE: GroupingAlterationField.Title,

            // section names
            MEDIA_VIEW: MEDIA_VIEW,
            ALTERATION_TYPE_VIEW: ALTERATION_TYPE_VIEW,
            ALTERATION_TITLE_VIEW: ALTERATION_TITLE_VIEW,

            // events
            DELETE_IMAGE_CLICK_TOPIC: DELETE_IMAGE_CLICK_TOPIC,
            MEDIA_ID_CHANGED_FROM_CACHE: MEDIA_ID_CHANGED_FROM_CACHE,
            PRINCIPAL_IMAGE_CLICK_TOPIC: PRINCIPAL_IMAGE_CLICK_TOPIC,
            ALTERATION_EDITED: ALTERATION_EDITED,
            GLOBAL_ALTERATION_CREATED: ALTERATION_CREATED,
            DELETE_ALTERATION: DELETE_ALTERATION,
            ALTERATION_CARD_ALTERATION_UPDATED: ALTERATION_CARD_ALTERATION_UPDATED,
            ALTERATION_CARD_DELETE_ALTERATION: ALTERATION_CARD_DELETE_ALTERATION,

            onDeleteAlteration: (() => {}) as Function,
            onAddMedia: (() => {}) as Function,
            onDeleteMedia: (() => {}) as Function,
            onChangeFirstMedia: (() => {}) as Function,
            onMediaIdChangeFromCache: (() => {}) as Function,
            onAlterationChanged: (() => {}) as Function,
            onAlterationCreated: (() => {}) as Function,
            onAlterationChangedCard: (() => {}) as Function,
            onDeleteAlterationCard: (() => {}) as Function,
        }
    },
    created() {
        // getListener() reads this.$listeners, unavailable to the this-context of data()
        this.onDeleteAlteration = this.getListener(DELETE_ALTERATION)
        this.onAddMedia = this.getListener('addMedia')
        this.onDeleteMedia = this.getListener(DELETE_IMAGE_CLICK_TOPIC)
        this.onChangeFirstMedia = this.getListener(PRINCIPAL_IMAGE_CLICK_TOPIC)
        this.onMediaIdChangeFromCache = this.getListener(MEDIA_ID_CHANGED_FROM_CACHE)
        this.onAlterationChanged = this.getListener(ALTERATION_EDITED)
        this.onAlterationCreated = this.getListener(ALTERATION_CREATED)
        this.onAlterationChangedCard = this.getListener(ALTERATION_CARD_ALTERATION_UPDATED)
        this.onDeleteAlterationCard = this.getListener(ALTERATION_CARD_DELETE_ALTERATION)
    },
    mounted() {
        this.sortAlterations()
    },
    methods: {
        getListener(key: string): Function {
            // @vue/compat's $listeners keys events camelCase, unlike Vue 2's raw event name;
            // check both forms.
            const camel = key.replace(/-(\w)/g, (_, c: string) => c.toUpperCase())
            const eventListener = (this.$listeners[key] || this.$listeners[camel]) as Function
            if(eventListener) {
                return eventListener
            }

            return () => {}
        },

        sortAlterations() {
            if(this.alterations) {
                const allAlterationTypes: (string|undefined)[] = _.uniq(
                    this.alterations.map(alteration => alteration.typeOfAlteration)
                )

                // $thesaurusTermDataStore is injected globally on Vue.prototype by ThesaurusTermDataStore's plugin install
                ;(this as any).$thesaurusTermDataStore.fetchMany2(
                    (allAlterationTypes.filter(thesaurusTermId => thesaurusTermId != null)) as string[],
                    this.getAppLocale()
                )
                    .subscribe((response) => {
                        let thesaurusTermsArray: ThesaurusTerm[] = []
                        if(response) {
                            if(Array.isArray(response)) {
                                thesaurusTermsArray = response
                            }
                            else if(response.data && Array.isArray(response.data)){
                                thesaurusTermsArray = response.data
                            }
                        }

                        this.alterationsByAlterationType = this.getAlterationsByAlterationTypes(allAlterationTypes, thesaurusTermsArray)
                        this.alterationsByTitle = this.getAlterationsByTitle(thesaurusTermsArray)
                    })
            } else {
                this.alterationsByAlterationType = {}
                this.alterationsByTitle = {}
            }
        },

        getAlterationsByAlterationTypes(allAlterationTypeIds: (string|undefined)[], alterationTypeThesaurusTerms: ThesaurusTerm[]):
            { [alterationTypeId: string]: (AlterationWithThesaurusTerm & { index?: number })[] } {

            let result: { [alterationTypeId: string]: (AlterationWithThesaurusTerm & { index?: number })[] } = {}

            if(this.alterations) {

                const alterationsWithIndex: (Alteration & { index?: number })[] = this.alterations.map((alteration, index) => {
                    return {...alteration, index}
                })

                for(const alterationTypeId of allAlterationTypeIds) {
                    const alterationTypeThesaurusTerm = alterationTypeThesaurusTerms?.find(thesaurusTerm => thesaurusTerm.id === alterationTypeId)
                    result[alterationTypeId ?? ''] = alterationsWithIndex
                            .filter(alteration => alteration.typeOfAlteration === alterationTypeId)
                            .map(alteration => {
                                return {
                                    ...alteration,
                                    typeOfAlterationObject: alterationTypeThesaurusTerm ?? null
                                } as AlterationWithThesaurusTerm & { index?: number }
                            })
                }
            }

            return result
        },

        getAlterationsByTitle(alterationTypeThesaurusTerms: ThesaurusTerm[]): { [title: string]: (AlterationWithThesaurusTerm & { index?: number })[] } {
            let result: { [title: string]: (AlterationWithThesaurusTerm & { index?: number })[] } = {}

            if(this.alterations) {
                const locale = this.getAppLocale()
                const allTitles = _.uniq(this.alterations.map(alteration => getTranslatedString(alteration.title, locale)))

                const alterationsWithIndex: (AlterationWithThesaurusTerm & { index?: number })[] = this.alterations.map((alteration, index) => {
                    return {
                        ...alteration,
                        index,
                        typeOfAlterationObject: alterationTypeThesaurusTerms.find(thesaurusTerm => thesaurusTerm.id === alteration.typeOfAlteration) ?? null,
                    }
                })

                for(const title of allTitles) {
                    result[title] = alterationsWithIndex.filter(alteration => getTranslatedString(alteration.title, locale) === title)
                }
            }

            return result
        },

        isActiveSection(sectionName: string) {
            return this.sections[this.activeSection] === sectionName
        },

        changeSection(sectionName: string) {
            const sectionIndex = this.sections.indexOf(sectionName)
            if(sectionIndex >= 0) {
                this.activeSection = sectionIndex
            }
            this.isViewMenuOpen = false
        },

        toggleViewMenu() {
            this.isViewMenuOpen = !this.isViewMenuOpen
        },
    },
    watch: {
        alterations: {
            handler() {
                this.sortAlterations()
            },
            deep: true,
        },
    },
})
